import { z } from "zod";
import { jsonSchema } from "./service-requests";

/**
 * Subscriptions shared contract (Strangler-Fig migration, manifest-6 slice a13).
 *
 * Single source of truth for the `subscriptions` / `subscription_plans` /
 * `subscription_pricing_tiers` / `pricing_settings` wire shapes shared by the
 * web SPA (`src/repositories/subscriptions.ts`) and the NestJS slice
 * (`apps/api/src/subscriptions/*`). Shapes are **snake_case**, byte-matching
 * today's PostgREST JSON (the `Tables<>` rows / `src/types/subscription.ts`)
 * so the FE↔Nest transport is a field-for-field swap.
 *
 * The web repo `.parse()`s Nest responses against these schemas at the flip
 * boundary, so any backend/frontend drift surfaces at runtime (and the repo
 * falls back to the Supabase path).
 *
 * apps/api imports from this package TYPE-only (a runtime value import
 * crash-loops the deployed API — see service-requests.repository.ts); the Nest
 * side re-declares any runtime enum locally.
 */

/** Mirrors the DB CHECK constraint (and `SubscriptionStatusEnum` in apps/api). */
export const SUBSCRIPTION_STATUSES = [
  "trial",
  "active",
  "canceled",
  "expired",
  "pending",
] as const;
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/** A persisted subscription row (one per company — company_id is DB-unique). */
export const subscriptionSchema = z.object({
  id: z.string(),
  company_id: z.string(),
  plan_id: z.string().nullable(),
  status: subscriptionStatusSchema,
  billing_cycle: z.string(),
  unit_limit: z.number(),
  user_limit: z.number(),
  balance: z.number(),
  trial_ends_at: z.string().nullable(),
  current_period_ends_at: z.string().nullable(),
  canceled_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  last_payment_attempt_at: z.string().nullable(),
  payment_failures: z.number().nullable(),
  grace_period_ends_at: z.string().nullable(),
});
export type SubscriptionWire = z.infer<typeof subscriptionSchema>;

/** Platform-level plan definition (no company scope). */
export const subscriptionPlanSchema = z.object({
  id: z.string(),
  name: z.string(),
  name_en: z.string().nullable(),
  description: z.string().nullable(),
  description_en: z.string().nullable(),
  trial_days: z.number(),
  annual_discount_percent: z.number(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SubscriptionPlanWire = z.infer<typeof subscriptionPlanSchema>;

/** Tiered pricing row for a plan (`tier_type` 'unit' | 'user' by convention). */
export const subscriptionPricingTierSchema = z.object({
  id: z.string(),
  plan_id: z.string(),
  tier_type: z.string(),
  min_count: z.number(),
  max_count: z.number().nullable(),
  price_per_unit: z.number(),
  created_at: z.string(),
});
export type SubscriptionPricingTierWire = z.infer<typeof subscriptionPricingTierSchema>;

/** Key→JSON platform pricing config row (super_admin authored). */
export const pricingSettingSchema = z.object({
  id: z.string(),
  key: z.string(),
  value: jsonSchema,
  description: z.string().nullable(),
  updated_by: z.string().nullable(),
  updated_at: z.string(),
  created_at: z.string(),
});
export type PricingSettingWire = z.infer<typeof pricingSettingSchema>;

/**
 * The single allowlisted pricing-setting `value` read available to any
 * authenticated caller (`GET /subscriptions/pricing-settings/service-catalog`).
 * Wrapped in an object so a null value (row absent) is representable in JSON.
 */
export const serviceCatalogValueSchema = z.object({
  value: jsonSchema.nullable(),
});
export type ServiceCatalogValueWire = z.infer<typeof serviceCatalogValueSchema>;

/** Subscription row + embedded company — the super-admin subscribers list. */
export const subscriptionWithCompanySchema = subscriptionSchema.extend({
  company: z
    .object({
      id: z.string(),
      name: z.string(),
      name_en: z.string().nullable(),
      email: z.string().nullable(),
      phone: z.string().nullable(),
    })
    .nullable(),
});
export type SubscriptionWithCompanyWire = z.infer<typeof subscriptionWithCompanySchema>;

/**
 * One server-side aggregate replacing the super-admin Dashboard's 5 discrete
 * subscription round-trips (`GET /subscriptions/admin/stats`).
 * `new_this_month_excluding_canceled` counts rows with
 * `created_at >= date_trunc('month', now())` — mirroring the FE's startOfMonth.
 */
export const subscriptionAdminStatsSchema = z.object({
  total_excluding_canceled: z.number(),
  active: z.number(),
  trial: z.number(),
  new_this_month_excluding_canceled: z.number(),
  active_balance_sum: z.number(),
});
export type SubscriptionAdminStatsWire = z.infer<typeof subscriptionAdminStatsSchema>;
